import React, { useEffect } from "react";
import * as THREE from "three";
import { TweenMax, Power1 } from "gsap";

const ThreeReveal = () => {
  useEffect(() => {
    let renderer, scene, camera;
    let objects = [];
    let conf = {
      color: 0xffffff,
      objectWidth: 12,
      objectThickness: 3,
      ambientLight: 0xffffff,
      light1Color: 0xffffff,
      perspective: 75,
      cameraZ: 75,
    };

    function init() {
      const canvas = document.createElement("canvas");
      canvas.id = "reveal-effect";
      document.body.appendChild(canvas);

      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
      });
      renderer.setSize(window.innerWidth, window.innerHeight);

      camera = new THREE.PerspectiveCamera(
        conf.perspective,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      );
      camera.position.z = conf.cameraZ;

      scene = new THREE.Scene();
      const geometry = new THREE.BoxGeometry(
        conf.objectWidth,
        conf.objectWidth,
        conf.objectThickness
      );

      initLights();
      initObjects(geometry);
      animate();
    }

    function initLights() {
      const ambientLight = new THREE.AmbientLight(0xffffff);
      scene.add(ambientLight);

      const pointLight = new THREE.PointLight(0xffffff, 2);
      pointLight.position.set(50, 50, 100);
      scene.add(pointLight);
    }

    function initObjects(geometry) {
      let wWidth = window.innerWidth;
      let wHeight = window.innerHeight;
      let nx = Math.round(wWidth / conf.objectWidth) + 1;
      let ny = Math.round(wHeight / conf.objectWidth) + 1;

      for (let i = 0; i < nx; i++) {
        for (let j = 0; j < ny; j++) {
          let material = new THREE.MeshLambertMaterial({
            color: 0xffffff,
            emissive: 0xffffff,
            transparent: true,
            emissiveIntensity: 0.7,
          });

          // Edge outline for sharp visibility
          let edges = new THREE.EdgesGeometry(geometry);
          let edgeMaterial = new THREE.LineBasicMaterial({
            color: 0xdddddd,
            linewidth: 2,
          });
          let edgeLines = new THREE.LineSegments(edges, edgeMaterial);

          let mesh = new THREE.Mesh(geometry, material);
          let x = -wWidth / 2 + i * conf.objectWidth;
          let y = -wHeight / 2 + j * conf.objectWidth;
          mesh.position.set(x, y, 0);
          edgeLines.position.set(x, y, 0);

          objects.push({ mesh, edgeLines });
          scene.add(mesh);
          scene.add(edgeLines);
        }
      }

      document.body.classList.add("loaded");
      startAnim();
    }

    function startAnim() {
      document.body.classList.remove("revealed");

      objects.forEach(({ mesh, edgeLines }) => {
        mesh.rotation.set(0, 0, 0);
        mesh.material.opacity = 1;
        mesh.position.z = 0;

        edgeLines.rotation.set(0, 0, 0);
        edgeLines.position.z = 0;

        let delay = Math.random() * 2;
        let rx = Math.random() * Math.PI * 2;
        let ry = Math.random() * Math.PI * 2;
        let rz = Math.random() * Math.PI * 2;

        TweenMax.to(mesh.rotation, 2, { x: rx, y: ry, z: rz, delay });
        TweenMax.to(mesh.position, 2, {
          z: 80,
          delay: delay + 0.5,
          ease: Power1.easeOut,
        });
        TweenMax.to(mesh.material, 2, { opacity: 0, delay: delay + 0.5 });

        TweenMax.to(edgeLines.rotation, 2, { x: rx, y: ry, z: rz, delay });
        TweenMax.to(edgeLines.position, 2, {
          z: 80,
          delay: delay + 0.5,
          ease: Power1.easeOut,
        });
        TweenMax.to(edgeLines.material, 2, { opacity: 0, delay: delay + 0.5 });
      });

      setTimeout(() => {
        document.body.classList.add("revealed");
      }, 4500);
    }

    function animate() {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    }

    init();

    return () => {
      document.getElementById("reveal-effect")?.remove();
    };
  }, []);

  return null;
};

export default ThreeReveal;
